import React, {useEffect} from "react";
import {useParams} from 'react-router-dom';
import {Button, Col, Form, InputGroup, Row} from "react-bootstrap";
import {useState} from "@hookstate/core";

import {
    formatDate, formatDateMMYY,
    formatPhoneNumber, formatUSD,
    getClient,
    getJob, getJobIdByNumber, setJobStatus, statusTypes,
} from "../../Services/ApiService";
import './JobDetails.scss';

function JobDetails() {
    const apiError = useState(false);
    const {jobId} = useParams();
    const job = useState({});
    const client = useState({});
    const noJobFound = useState(false);
    const referencedJobId = useState(undefined);
    const referencedJobDate = useState(undefined);

    useEffect(() => {
        getJob(jobId, _job => {
            if (_job === false) return apiError.set(true);

            if (_job[0] === undefined) {
                noJobFound.set(true);
            } else {
                job.set(_job[0]);
                getClient(_job[0].clientId, _client => {
                    client.set(_client[0]);
                });

                if (_job[0].jobReferenced !== '') {
                    getJobIdByNumber(_job[0].jobReferenced, _id => {
                        if (_id !== false && _id[0].id !== false) {
                            referencedJobId.set(_id[0].id)

                            getJob(referencedJobId.get(), _refJob => {
                                referencedJobDate.set(_refJob[0].dateReceived);
                            })
                        }
                    })
                }
            }
        });
        // eslint-disable-next-line
    }, []);

    // <editor-fold description="modulars">
    function printCell(_header, _value, _cellSize = 0) {
        return <Col className={'cell'} sm={_cellSize}>
                <p className={'header'}>{_header}</p>
                <p className={'value'}>{_value}</p>
            </Col>
    }

    function jobDetailsRow() {
        return <Row id={'jobDetailsRow'}>
            <Col className={'cell'} id={'jobNumberCell'}>
                <p className={'header'}>Job Number</p>
                <p className={'value'}>
                    {job.get().jobNumber > 0 ? job.get().jobNumber + job.get().jobNumberAppend : "Estimate"}
                </p>
            </Col>
            {printCell('Customer PO', job.get().customerPO)}
            {printCell('Salesman', job.get().salesman)}
            {printCell('Date Received', formatDate(job.get().dateReceived))}
            {printCell('Date Due', formatDate(job.get().dateDue))}
        </Row>
    }

    function RepeatedJobLink() {
        if (referencedJobId.get() === undefined) {
            return <p>Repeat of our order: {job.get().jobReferenced}</p>
        } else {
            return <p>Repeat of our order: <a href={`/job/${referencedJobId.get()}`}>{job.get().jobReferenced}</a> {formatDateMMYY(referencedJobDate.get())}</p>
        }
    }

    function repeatDetailsRow() {
        return <Row id={'repeatDetailsRow'}>
            <Col id={'repeatDetails'} className={'cell'}>
                <Col>
                    <div>
                        <label>New
                            <Form.Check label={'Include Specification'} checked={job.get().isNewJob} readOnly />
                        </label>
                    </div>
                    {RepeatedJobLink()}
                </Col>
                <Col>
                    <Form.Check label={'Without Copy Change'} checked={!job.get().isCopyChange} readOnly />
                    <Form.Check label={'With Copy Change'} checked={job.get().isCopyChange} readOnly />
                </Col>
                <Col>
                    <Form.Check label={'Without Spec Change'} checked={!job.get().isSpecChange} readOnly />
                    <Form.Check label={'With Spec Change'} checked={job.get().isSpecChange} readOnly />
                </Col>
            </Col>
            <Col className={'cell'} sm={1}>
                <Form.Check label={'Proof'} checked={job.get().isProofRequested} readOnly />
            </Col>
        </Row>
    }

    function clientBillingDetailsRow(links = true) {
        return <Row id={'clientDetailsRow'}>
            <Col className={'cell'} id={'soldTo'}>
                <div id={'billingDetails'}>
                    <div id={'topLine'}>
                        <p className={'header'}>Sold To:</p>
                        {links ?
                            <p id={'clientName'}><a href={`/client/${job.get().clientId}`}>{client.get().name}</a></p>
                            : <p id={'clientName'}>{client.get().name}</p>
                        }
                    </div>
                    {job.get().billingAddressStreet !== '' &&
                        <div>
                            <p>{job.get().billingAddressStreet}</p>
                            <p>{job.get().billingAddressStreet2}</p>
                            <p>{job.get().billingAddressCity !== "" ? job.get().billingAddressCity + ", " : ""}{job.get().billingAddressState} {job.get().billingAddressZip}</p>
                        </div>
                    }
                </div>
            </Col>

            <Col className={'cell'} id={'billingContact'}>
                <p className={'header'}>Contact:</p>
                {links ?
                    <div id={'billingContactDetails'}>
                        <p>Name: {job.get().billingContactName}</p>
                        <p>Phone: <a
                            href={`tel:${job.get().billingContactPhone}`}>{formatPhoneNumber(job.get().billingContactPhone)}</a>
                        </p>
                        <p>Email: <a
                            href={`mailto:${job.get().billingContactEmail}`}>{job.get().billingContactEmail}</a></p>
                    </div>
                    : <div id={'billingContactDetails'}>
                        <p>Name: {job.get().billingContactName}</p>
                        <p>Phone: {formatPhoneNumber(job.get().billingContactPhone)}</p>
                        <p>Email: {job.get().billingContactEmail}</p>
                    </div>
                }
            </Col>
        </Row>
    }

    function jobItemsRow() {
        return (
            <Row id={'jobItemsRow'}>
                {printCell("Quantity", job.get().quantity)}
                {printCell("Num Pages", job.get().numPages)}
                {printCell("Flat Size", job.get().flatSize)}
                {printCell("Final Size", job.get().finSize)}
                {printCell("Num Folds", job.get().numFolds)}
                <Col className={'cell'} id={'jobTitleCell'} sm={4}>
                    <p id={'jobTitle'}>{job.get().itemInfo}</p>
                </Col>
            </Row>
        )
    }

    function materialsRow() {
        // noinspection RequiredAttributes
        return (
            <Row>
                {printCell("Materials", job.get().materials, 8)}
                <Col className={'cell'} id={'duplexCell'}>
                    <label className={'header'}>Prints:</label>
                    <div id={'isDuplexRadio'}>
                        <Form.Label className={'isDuplexRadioDiv'}>
                            <Form.Check type={'radio'}
                                        checked={!job.get().isDuplex} readOnly />
                            One Side
                        </Form.Label>
                        <Form.Label className={'isDuplexRadioDiv'}>
                            <Form.Check type={'radio'}
                                        checked={job.get().isDuplex} readOnly />
                            Two Sides
                        </Form.Label>
                    </div>
                </Col>
            </Row>
        )
    }

    function inksRow(print = false) {
        if (!print) {
            return (
                <Row id={'inksRow'}>
                    <Col className={'cell'}>
                        <InputGroup id={'inksGroup'}>
                            <InputGroup.Append><InputGroup.Text>Inks</InputGroup.Text></InputGroup.Append>
                            <Form.Control id={'inks'} value={job.get().inks} readOnly/>
                        </InputGroup>
                    </Col>

                    <Col className={'cell'}>
                        <InputGroup id={'inksGroup'}>
                            <InputGroup.Append><InputGroup.Text>Outside Service</InputGroup.Text></InputGroup.Append>
                            <Form.Control id={'outsideServices'} value={job.get().outsideServices} readOnly/>
                        </InputGroup>
                    </Col>

                    <Col className={'cell'} id={'press'} sm={4}>
                        <label className={'header'}>Press:</label>
                        <div id={'pressRadiosCell'}>
                            <div id={'radios'}>
                                <label>
                                    <Form.Check name={`pressId`}
                                                id={`offset`}
                                                type={'radio'}
                                                checked={job.get().pressId === 0}
                                                readOnly
                                    />
                                    Offset
                                </label>
                                <label>
                                    <Form.Check name={`pressId`}
                                                id={`signShop`}
                                                type={'radio'}
                                                checked={job.get().pressId === 1}
                                                readOnly
                                    />
                                    Sign Shop
                                </label>
                            </div>
                            <div id={'radios'}>
                                <label>
                                    <Form.Check name={`pressId`}
                                                id={`digital`}
                                                type={'radio'}
                                                checked={job.get().pressId === 2}
                                                readOnly
                                    />
                                    Digital
                                </label>
                                <label>
                                    <Form.Check name={`pressId`}
                                                id={`outsidePrinter`}
                                                type={'radio'}
                                                checked={job.get().pressId === 3}
                                                readOnly
                                    />
                                    Outside Printer
                                </label>
                            </div>
                        </div>

                        {job.get().pressId === 3 &&
                            <Form.Control id={`outsidePrinter`} value={job.get().outsidePrinter} readOnly/>
                        }
                    </Col>
                </Row>
            )
        }
        else {
            const pressArr = ["Offset", "Sign Shop", "Digital", `Outside Printer: ${job.get().outsidePrinter}`];

            return (
                <Row id={'inksRow'}>
                    {printCell("Inks", job.get().inks)}
                    {printCell("Outside Service", job.get().outsideServices)}
                    <Col className={'cell'} id={'press'} sm={4}>
                        <p className={'header'}>Press</p>
                        <p>{pressArr[job.get().pressId]}</p>
                    </Col>
                </Row>
            )
        }
    }

    function additionalSpecsRow() {
        return <Row id={`additionalSpecsRow`}>
            <Col>
                <p className={'header'}>Additional Specifications</p>
                <p className={'value'}>{job.get().additionalSpecs}</p>
            </Col>
        </Row>;
    }

    function additionalOptionsRow() {
        return  (
            <div id={'additionalOptions'}>
                <Row id={'generic'}>
                    <div><Form.Check className={'col cell'} label={'Scoring'} checked={job.get().isScoring} readOnly/></div>
                    <div><Form.Check className={'col cell'} label={'Diecut'} checked={job.get().isDiecut} readOnly/></div>
                    <div><Form.Check className={'col cell'} label={'Flat'} checked={job.get().isFlat} readOnly/></div>
                    <div><Form.Check className={'col cell'} label={'Glue'} checked={job.get().isGlue} readOnly/></div>
                </Row>

                <Row id={'nonGeneric'}>
                    <Col className={'cell'} id={'binding'}>
                        <Form.Check label={'Binding'} checked={job.get().isBinding} readOnly />
                        {job.get().isBinding === 1 &&
                            <div className={'options'}>
                                <Col>
                                    <p className={'header'}>Style</p>
                                    <p className={'value'}>{job.get().bindingStyle}</p>
                                </Col>
                                <Col>
                                    <p className={'header'}>Location</p>
                                    <p className={'value'}>{job.get().bindingLoc}</p>
                                </Col>
                            </div>
                        }
                    </Col>
                    <Col className={'cell'} id={'padding'}>
                        <Form.Check label={'Padding'} checked={job.get().isPadding} readOnly />
                        {job.get().isPadding === 1 &&
                            <div className={'options'}>
                                <Col>
                                    <p className={'header'}>Sets of</p>
                                    <p className={'value'}>{job.get().paddingSetsOf}</p>
                                </Col>
                            </div>
                        }
                    </Col>
                    <Col className={'cell'} id={'perf'}>
                        <Form.Check label={'Perforations'} checked={job.get().isPerfed} readOnly />
                        {job.get().isPerfed === 1 &&
                            <div className={'options'}>
                                <Col>
                                    <p className={'header'}>Style</p>
                                    <p className={'value'}>{job.get().perfStyle}</p>
                                </Col>
                            </div>
                        }
                    </Col>
                    <Col className={'cell'} id={'punching'}>
                        <Form.Check label={'Punching'} checked={job.get().isPunched} readOnly />
                        {job.get().isPunched === 1 &&
                            <div className={'options'}>
                                <Col>
                                    <p className={'header'}>Location</p>
                                    <p className={'value'}>{job.get().punchingLoc}</p>
                                </Col>
                                <Col>
                                    <p className={'header'}>Style</p>
                                    <p className={'value'}>{job.get().punchingStyle}</p>
                                </Col>
                            </div>
                        }
                    </Col>
                    <Col className={'cell'} id={'numbering'}>
                        <Form.Check label={'Numbering'} checked={job.get().isNumbered} readOnly />
                        {job.get().isNumbered === 1 &&
                            <div className={'options'}>
                                <Col>
                                    <p className={'header'}>Location</p>
                                    <p className={'value'}>{job.get().numberingLoc}</p>
                                </Col>
                                <Col>
                                    <p className={'header'}>Numbers</p>
                                    <p className={'value'}>{job.get().numberingNumbers}</p>
                                </Col>
                            </div>
                        }
                    </Col>
                    <Col className={'cell'} id={'roundCorner'}>
                        <Form.Check label={'Round Corner'} checked={job.get().isRoundCorner} readOnly />
                        {job.get().isRoundCorner === 1 &&
                            <div className={'options'}>
                                <Col>
                                    <p className={'header'}>Location</p>
                                    <p className={'value'}>{job.get().roundCornerLoc}</p>
                                </Col>
                            </div>
                        }
                    </Col>
                </Row>
            </div>
        )
    }

    function deliveryDetailsRow(links = true) {
        return <Row id={'deliveryDetailsRow'}>
            <Col className={'cell'} id={'deliverTo'}>
                <div id={'deliveryHeader'}>
                    <p className={'header'}>Deliver To:</p>
                    <div id={'isPickupCheck'}>
                        <Form.Check id={'isPickup'}
                                    checked={job.get().isPickup}
                                    label={'Client Picking Up'}
                                    readOnly />
                    </div>
                </div>
                {job.get().shippingAddressStreet !== '' &&
                    <div id={'shippingDetails'}>
                        <p>{job.get().shippingAddressStreet}</p>
                        <p>{job.get().shippingAddressStreet2}</p>
                        <p>{job.get().shippingAddressCity !== "" ? job.get().shippingAddressCity + ", " : ""}{job.get().shippingAddressState} {job.get().shippingAddressZip}</p>
                    </div>
                }
            </Col>

            <Col className={'cell'} id={'shippingContact'}>
                <p className={'header'}>Contact:</p>
                {links ?
                    <div id={'shippingContactDetails'}>
                        <p>Name: {job.get().shippingContactName}</p>
                        <p>Phone: <a
                            href={`tel:${job.get().shippingContactPhone}`}>{formatPhoneNumber(job.get().shippingContactPhone)}</a>
                        </p>
                        <p>Email: <a
                            href={`mailto:${job.get().shippingContactEmail}`}>{job.get().shippingContactEmail}</a></p>
                    </div> :
                    <div id={'shippingContactDetails'}>
                        <p>Name: {job.get().shippingContactName}</p>
                        <p>Phone: {formatPhoneNumber(job.get().shippingContactPhone)}</p>
                        <p>Email: {job.get().shippingContactEmail}</p>
                    </div>
                }
            </Col>
        </Row>
    }

    function shippingServiceDetailsRow() {
        return <Row id={'shippingServiceDetailsRow'}>
            <Col className={'cell'}>
                <p className={'header'}>Packing</p>
                <p className={'value'}>{job.get().packingType}</p>
            </Col>
            <Col>
                <p className={'header'}>Units of</p>
                <p className={'value'}>{job.get().packingUnitsOf}</p>
            </Col>
            <Col className={'cell'} id={'shipping'}>
                <p className={'header'}>Ship Via:</p>
                <p className={'value'}>{job.get().shipVia}</p>
            </Col>
            <Col>
                <Form.Check label={'Prepaid'} checked={job.get().isPrepaid} readOnly />
                <Form.Check label={'Collect'} checked={job.get().isPrepaid} readOnly />
            </Col>
            <Col className={'cell col-2'} id={'outsideServicePO'}>
                <p className={'header'}>Outside Service PO</p>
                <p className={'value'}>{job.get().poOutsideService}</p>
            </Col>
            <Col className={'cell col-2 noPrint'}>
                <p className={'header'}>File number</p>
                <p className={'value'}>{job.get().fileNumber}</p>
            </Col>
        </Row>
    }

    function accountingRow(print = false) {
        if (!print) {
            return <Row id={'accountingRow'}>
                <Col className={'cell'} id={'previous'}>
                    <Form.Check label={'Price Quoted'} checked={job.get().isPriceQuoted} readOnly/>
                    <Col>
                        <p className={'header'}>Last Printing Quantity</p>
                        <p className={'value'}>{job.get().lastPrintingQuantity}</p>
                    </Col>
                    <Col>
                        <p className={'header'}>Last Printing Price</p>
                        <p className={'value'}>{job.get().lastPrintingPrice}</p>
                    </Col>
                </Col>
                <Col className={'cell'} id={'pricing'}>
                    <InputGroup>
                        <InputGroup.Append><InputGroup.Text>Amount Net</InputGroup.Text></InputGroup.Append>
                        <Form.Control id={'amountNet'} value={formatUSD(job.get().amountNet)} readOnly/>
                    </InputGroup>
                    <InputGroup>
                        <InputGroup.Append><InputGroup.Text>Sales Tax</InputGroup.Text></InputGroup.Append>
                        <Form.Control id={'salesTax'} value={formatUSD(job.get().salesTax)} readOnly/>
                    </InputGroup>
                    <InputGroup>
                        <InputGroup.Append><InputGroup.Text>Postage</InputGroup.Text></InputGroup.Append>
                        <Form.Control id={'postage'} value={formatUSD(job.get().postage)} readOnly/>
                    </InputGroup>
                    <InputGroup>
                        <InputGroup.Append><InputGroup.Text>Shipping Charges</InputGroup.Text></InputGroup.Append>
                        <Form.Control id={'shippingFees'} value={formatUSD(job.get().shippingFees)} readOnly/>
                    </InputGroup>
                    <InputGroup>
                        <InputGroup.Append><InputGroup.Text>Total Amount</InputGroup.Text></InputGroup.Append>
                        <Form.Control id={'totalAmount'} value={formatUSD(job.get().totalAmount)} readOnly/>
                    </InputGroup>
                </Col>
            </Row>
        } else {
            return <Row id={'accountingRow'}>
                <Col className={'cell col-7'} id={'previous'}>
                    <div id={"priceQuoted"}>
                        <Form.Check label={'Price Quoted'} checked={job.get().isPriceQuoted} readOnly/>
                    </div>
                    <Col>
                        <p className={'header'}>Last Printing Quantity</p>
                        <p className={'value'}>{job.get().lastPrintingQuantity}</p>
                    </Col>
                    <Col>
                        <p className={'header'}>Last Printing Price</p>
                        <p className={'value'}>{job.get().lastPrintingPrice}</p>
                    </Col>
                </Col>
                <Col className={'cell'} id={'pricing'}>
                    <table>
                        <tbody>
                            {/*<tr><td className={'header'}>Amount Gross:</td><td className={'value'}>{job.get().amountGross}</td></tr>*/}
                            <tr><td className={'header'}>Amount Net:</td><td className={'value'}>{formatUSD(job.get().amountNet)}</td></tr>
                            <tr><td className={'header'}>Sales Tax:</td><td className={'value'}>{formatUSD(job.get().salesTax)}</td></tr>
                            <tr><td className={'header'}>Shipping Charges:</td><td className={'value'}>{formatUSD(job.get().shippingFees)}</td></tr>
                            <tr><td className={'header'}>Postage:</td><td className={'value'}>{formatUSD(job.get().postage)}</td></tr>
                            <tr><td className={'header'}>Total Amount:</td><td className={'value'}>{formatUSD(job.get().totalAmount)}</td></tr>
                        </tbody>
                    </table>
                </Col>
            </Row>
        }
    }

    function paperUsedRow() {
        return <Row id={'bottomRow'}>
            <Col className={'cell'} id={'paperUsed'} sm={9}>
                <table id={'paperTable'}>
                    <tbody>
                        <tr>
                            <th id={'paper'} colSpan={12}>Paper Used / Ordered</th>
                        </tr>
                        <tr>
                            <th className={'numSheets'}>No. Sheets</th>
                            <th className={'description'}>Description</th>
                            <th className={'pricePerM'}>$/M</th>
                            <th className={'priceTotal'}>Total $</th>
                        </tr>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </Col>
            <Col className={'cell'} id={'finalDetails'}>
                <div id={'pressCount'}>
                    <p className={'header'}>Press Count</p>
                </div>
                <div id={'impressions'}>
                    <p className={'header'}>Impressions</p>
                </div>
                <div id={'plateFile'}>
                    <p className={'header'}>Plate File</p>
                </div>
                <div id={'fileNumber'}>
                    <p className={'header'}>File Number</p>
                    <p className={'value'}>{job.get().fileNumber}</p>
                </div>
            </Col>
        </Row>
    }
    // </editor-fold description="modulars">

    const savedNotifIsHidden = useState(true);

    function updateJobStatus(e) {
        const newStatus = e.target.value;

        // noinspection JSUnresolvedReference
        job.status.set(newStatus);
        setJobStatus(newStatus, job.get().id,
            newDate => {
                if (newStatus === "Printed") {
                    job.printedDate.set(newDate);
                    job.invoicedDate.set(null);
                } else if (newStatus === "Invoiced") {
                    job.invoicedDate.set(newDate);
                } else {
                    job.printedDate.set(null);
                    job.invoicedDate.set(null);
                }

                savedNotifIsHidden.set(false);

                setTimeout(() => {
                    savedNotifIsHidden.set(true);
                }, 1000)
            })
    }

    // <editor-fold desc="final render functions">
    function display() {
        // noinspection JSUnresolvedReference
        return <div id={'display'} className={'noPrint'}>
            {/*<Button onClick={_ => {*/}
            {/*    console.log(job.get().id)}}>Test</Button>*/}

            <div id={'topButtons'}>
                <div id={'left'}>
                    <Button href={`/newJob?repeat=${job.get().id}`}>Repeat this Job</Button>
                    <Button href={`/newJob?edit=${job.get().id}`}>Edit</Button>
                </div>
                <div id={'right'}>
                    <Button id={'printButton'} onClick={() => {window.print()}}>Print</Button>
                </div>
            </div>

            <Col id={'statusDiv'}>
                <div id={'statusDivInner'}>
                    <div>
                        <label htmlFor={'statusSelect'}>Status: </label>
                        <select className={'form-select'} id={'statusSelect'}
                                value={job.get().status}
                                onChange={e => updateJobStatus(e)}
                        >
                            {statusTypes.map(status => {
                                return <option key={status} value={status}>{status}</option>
                            })}
                        </select>
                    </div>
                    <div id={"statusDates"}>
                        <div id={`printedDate-${job.get().printedDate !== null}`}>
                            <p>Printed: {formatDate(job.get().printedDate)}</p>
                        </div>
                        <div id={`invoicedDate-${job.get().invoicedDate !== null}`}>
                            <p>Invoiced: {formatDate(job.get().invoicedDate)}</p>
                        </div>
                    </div>
                </div>
                <p id={`savedNotification-${savedNotifIsHidden.get()}`}>Status saved</p>
            </Col>

            {jobDetailsRow()}
            {repeatDetailsRow()}
            {clientBillingDetailsRow()}
            {jobItemsRow()}
            {materialsRow()}
            {inksRow()}
            {additionalOptionsRow()}
            {additionalSpecsRow()}
            {deliveryDetailsRow()}
            {shippingServiceDetailsRow()}
            {accountingRow()}
        </div>
    }

    function printPage(pageType = "Accounting") {
        return <div id={`page${pageType}`}>
            <div className={'printTop'}>
                {jobDetailsRow()}
                {clientBillingDetailsRow(false)}
                {repeatDetailsRow()}
                {jobItemsRow()}
                {materialsRow()}
                {inksRow(true)}
                {additionalOptionsRow()}
                {additionalSpecsRow()}
            </div>
            <footer id={'printBottom'} className={pageType === "Jacket" ? 'noPrint' : ""}>
                {deliveryDetailsRow(false)}
                {shippingServiceDetailsRow()}
                {accountingRow(true)}
            </footer>
            <footer id={'printBottom2'} className={pageType !== "Jacket" ? 'noPrint' : ""}>
                {deliveryDetailsRow(false)}
                {shippingServiceDetailsRow()}
                {paperUsedRow()}
            </footer>
        </div>
    }
    // </editor-fold desc="Final render functions">


    // Actual page/print renderer
    if (apiError.get()) {
        return <h4>Unable to reach server.</h4>;
    }

    // if (retrieved.get().length < 4) {
    //     return <p>Loading...</p>
    // } else {
        return <div className='jobDetails'>
            {display()}

            <div id={'print'}>
                {printPage()}
                {job.get().isEstimate !== 1 && printPage("Jacket")} {/* Job Jacket page */}
            </div>
        </div>
    // }
}

export default JobDetails;